import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { useRecordContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import JsonEditorAce from '../../components/code/ace/json.editor';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SchemaIcon from '@mui/icons-material/Schema';
import DataObjectIcon from '@mui/icons-material/DataObject';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

interface SchemaVisualRendererProps {
  jsonSchema?: string;
}

export const SchemaVisualRenderer: React.FC<SchemaVisualRendererProps> = ({ jsonSchema: propJsonSchema }) => {
  const record = useRecordContext();
  const formContext = useFormContext();
  
  // Get JSON schema from props, form values, or record (in that priority order)
  const currentJsonSchema = propJsonSchema || 
    formContext?.getValues?.()?.jsonSchema || 
    record?.jsonSchema;

  console.log('SchemaVisualRenderer debug:', {
    propJsonSchema,
    formContextJsonSchema: formContext?.getValues?.()?.jsonSchema,
    recordJsonSchema: record?.jsonSchema,
    currentJsonSchema,
    hasCurrentJsonSchema: !!currentJsonSchema
  });

  if (!record) {
    return (
      <Typography variant="body2" color="textSecondary">
        No schema record available
      </Typography>
    );
  }

  let parsedSchema;
  try {
    parsedSchema = currentJsonSchema ? JSON.parse(currentJsonSchema) : null;
  } catch (error) {
    return (
      <Card sx={{ bgcolor: 'error.light' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <ErrorIcon sx={{ mr: 1, color: 'error.main' }} />
            <Typography variant="h6" color="error.main">
              Invalid JSON Schema
            </Typography>
          </Box>
          <Typography variant="body2">
            The JSON Schema contains syntax errors and cannot be parsed.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!parsedSchema) {
    return (
      <Typography variant="body2" color="textSecondary">
        No JSON Schema defined for this schema
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Schema Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SchemaIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              {parsedSchema.title || record.name}
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            {parsedSchema.description || record.description}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip 
                  label={`Type: ${parsedSchema.type || 'object'}`} 
                  variant="outlined" 
                  size="small" 
                />
                <Chip 
                  label={`Domain: ${record.domain || 'unknown'}`} 
                  variant="outlined" 
                  size="small" 
                />
                {record.configuration?.isCore && (
                  <Chip 
                    label="Core Schema" 
                    color="primary" 
                    size="small" 
                  />
                )}
                {record.configuration?.allowExtensions && (
                  <Chip 
                    label="Extensible" 
                    color="secondary" 
                    size="small" 
                  />
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                <strong>Version:</strong> {record.currentVersion || '1.0.0'}<br/>
                <strong>Properties:</strong> {Object.keys(parsedSchema.properties || {}).length}<br/>
                <strong>Required Fields:</strong> {(parsedSchema.required || []).length}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Properties Table */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DataObjectIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Properties ({Object.keys(parsedSchema.properties || {}).length})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <SchemaPropertiesTable schema={parsedSchema} />
        </AccordionDetails>
      </Accordion>

      {/* Validation Rules */}
      {parsedSchema.required && parsedSchema.required.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon sx={{ mr: 1 }} />
              <Typography variant="h6">
                Validation Rules
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <SchemaValidationRules schema={parsedSchema} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Definitions/References */}
      {parsedSchema.definitions && Object.keys(parsedSchema.definitions).length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LinkIcon sx={{ mr: 1 }} />
              <Typography variant="h6">
                Definitions ({Object.keys(parsedSchema.definitions).length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <SchemaDefinitions definitions={parsedSchema.definitions} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Examples */}
      {parsedSchema.examples && parsedSchema.examples.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              Examples ({parsedSchema.examples.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SchemaExamples examples={parsedSchema.examples} />
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

// Properties Table Component
const SchemaPropertiesTable: React.FC<{ schema: any }> = ({ schema }) => {
  if (!schema.properties || Object.keys(schema.properties).length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No properties defined
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Property</strong></TableCell>
            <TableCell><strong>Type</strong></TableCell>
            <TableCell><strong>Required</strong></TableCell>
            <TableCell><strong>Format</strong></TableCell>
            <TableCell><strong>Description</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(schema.properties).map(([key, prop]: [string, any]) => (
            <TableRow key={key}>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {key}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip 
                  label={getPropertyType(prop)} 
                  size="small" 
                  variant="outlined"
                  color={getTypeColor(prop.type)}
                />
                {prop.format && (
                  <Chip 
                    label={prop.format} 
                    size="small" 
                    variant="outlined"
                    sx={{ ml: 0.5 }}
                  />
                )}
              </TableCell>
              <TableCell>
                {schema.required?.includes(key) ? (
                  <Chip label="Required" size="small" color="error" />
                ) : (
                  <Chip label="Optional" size="small" color="default" />
                )}
              </TableCell>
              <TableCell>
                {prop.format || '-'}
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {prop.description || '-'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Validation Rules Component
const SchemaValidationRules: React.FC<{ schema: any }> = ({ schema }) => {
  const rules: string[] = [];

  if (schema.required && schema.required.length > 0) {
    rules.push(`Required fields: ${schema.required.join(', ')}`);
  }

  if (schema.additionalProperties === false) {
    rules.push('No additional properties allowed (strict validation)');
  }

  // Check for validation rules in properties
  Object.entries(schema.properties || {}).forEach(([key, prop]: [string, any]) => {
    if (prop.minimum !== undefined) {
      rules.push(`${key}: minimum value ${prop.minimum}`);
    }
    if (prop.maximum !== undefined) {
      rules.push(`${key}: maximum value ${prop.maximum}`);
    }
    if (prop.minLength !== undefined) {
      rules.push(`${key}: minimum length ${prop.minLength}`);
    }
    if (prop.maxLength !== undefined) {
      rules.push(`${key}: maximum length ${prop.maxLength}`);
    }
    if (prop.pattern) {
      rules.push(`${key}: pattern ${prop.pattern}`);
    }
    if (prop.enum) {
      rules.push(`${key}: allowed values [${prop.enum.join(', ')}]`);
    }
  });

  return (
    <List>
      {rules.map((rule, index) => (
        <ListItem key={index}>
          <ListItemText primary={rule} />
        </ListItem>
      ))}
      {rules.length === 0 && (
        <ListItem>
          <ListItemText primary="No specific validation rules defined" />
        </ListItem>
      )}
    </List>
  );
};

// Schema Definitions Component
const SchemaDefinitions: React.FC<{ definitions: any }> = ({ definitions }) => {
  return (
    <Box>
      {Object.entries(definitions).map(([key, def]: [string, any]) => (
        <Box key={key} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', mb: 1 }}>
            {key}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            {def.description || 'Reusable schema definition'}
          </Typography>
          <Divider sx={{ my: 1 }} />
        </Box>
      ))}
    </Box>
  );
};

// Schema Examples Component
const SchemaExamples: React.FC<{ examples: any[] }> = ({ examples }) => {
  return (
    <Box>
      {examples.map((example, index) => (
        <Box key={index} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Example {index + 1}
          </Typography>
          <Box sx={{ 
            height: '300px',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden'
          }}>
            <JsonEditorAce
              value={JSON.stringify(example, null, 2)}
              onChange={() => {}} // Read-only by ignoring changes
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// Helper functions
const getPropertyType = (prop: any): string => {
  if (prop.type === 'array') {
    return `${prop.items?.type || 'any'}[]`;
  }
  if (prop.$ref) {
    return prop.$ref.split('/').pop() || 'reference';
  }
  return prop.type || 'any';
};

const getTypeColor = (type: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (type) {
    case 'string': return 'info';
    case 'number': return 'success';
    case 'boolean': return 'warning';
    case 'object': return 'primary';
    case 'array': return 'secondary';
    default: return 'default';
  }
};
